/**
 * Product Reviews – Custom Yotpo Integration
 *
 * Fetches reviews from the Yotpo public widget API and renders
 * a fully interactive review section with:
 *   - Rating snapshot (histogram)
 *   - Overall rating summary
 *   - Star-based review submission form
 *   - Filtering (rating, employee, search)
 *   - Sorting & pagination
 *   - Helpful vote & report actions
 */

(function () {
  'use strict';

  const CONTAINER = document.getElementById('product-reviews');
  if (!CONTAINER) return;

  const I18N = (() => {
    const el = document.getElementById('product-reviews-i18n');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('Invalid product-reviews-i18n JSON', e);
      return {};
    }
  })();

  /** @param {string} key @param {Record<string, string | number>} [vars] */
  function t(key, vars = {}) {
    let s = I18N[key];
    if (typeof s !== 'string') return key;
    return s.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
  }

  function starLabel(n, oneKey, otherKey) {
    return t(n === 1 ? oneKey : otherKey, { n });
  }

  const LOCALE = CONTAINER.dataset.locale || 'en';

  const APP_KEY = CONTAINER.dataset.appKey;
  const PRODUCT_ID = CONTAINER.dataset.productId;
  const PRODUCT_TITLE = CONTAINER.dataset.productTitle;
  const PRODUCT_URL = CONTAINER.dataset.productUrl;
  const PRODUCT_IMAGE_URL = CONTAINER.dataset.productImageUrl || '';
  const DOMAIN = CONTAINER.dataset.domain;
  const PER_PAGE = parseInt(CONTAINER.dataset.perPage || '5', 10) || 5;
  const API_BASE = 'https://api.yotpo.com/v1/widget';
  const CLOUDINARY_CLOUD = CONTAINER.dataset.cloudinaryCloud || '';
  const CLOUDINARY_PRESET = CONTAINER.dataset.cloudinaryPreset || '';

  if (!APP_KEY || !PRODUCT_ID) {
    CONTAINER.innerHTML = '';
    return;
  }

  /* =========================================================
     State
     ========================================================= */
  let state = {
    reviews: [],
    allReviews: [],
    allReviewsLoaded: false,
    bottomline: null,
    page: 1,
    totalReviews: 0,
    sort: 'date',
    direction: 'desc',
    starFilter: null,
    searchQuery: '',
    loading: true,
    selectedImages: [], // [{ file, url }]
  };

  const MAX_IMAGES = 5;
  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  function addImageFiles(files) {
    const room = MAX_IMAGES - state.selectedImages.length;
    const errors = [];
    let added = 0;
    for (const file of files) {
      if (added >= room) {
        errors.push(`Only ${MAX_IMAGES} photos allowed`);
        break;
      }
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        errors.push(`${file.name}: unsupported type`);
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        errors.push(`${file.name}: exceeds 5MB`);
        continue;
      }
      state.selectedImages.push({ file, url: URL.createObjectURL(file) });
      added++;
    }
    return errors;
  }

  function removeImageAt(idx) {
    const img = state.selectedImages[idx];
    if (img) {
      URL.revokeObjectURL(img.url);
      state.selectedImages.splice(idx, 1);
    }
  }

  function clearSelectedImages() {
    for (const img of state.selectedImages) URL.revokeObjectURL(img.url);
    state.selectedImages = [];
  }

  // Refreshes only the photo list inside the form. Avoids calling the full
  // render(), which would rebuild the modal overlay and drop its open state.
  function renderPhotoList() {
    const list = document.getElementById('review-photo-list');
    if (!list) return;
    const thumbs = state.selectedImages.map((img, idx) => `
      <div class="product-reviews__photo-thumb" data-idx="${idx}">
        <img src="${img.url}" alt="">
        <button type="button" class="product-reviews__photo-remove" aria-label="Remove photo">&times;</button>
      </div>`).join('');
    const addBtn = state.selectedImages.length < MAX_IMAGES ? `
      <label class="product-reviews__photo-add" for="review-photo-input">
        <span class="product-reviews__photo-add-icon">+</span>
        <span>Add</span>
      </label>` : '';
    list.innerHTML = thumbs + addBtn;
  }

  /* =========================================================
     Local vote persistence (one vote per review, survives refresh)
     ========================================================= */
  const VOTES_STORAGE_KEY = 'yotpo_review_votes';

  function getStoredVotes() {
    try {
      return JSON.parse(localStorage.getItem(VOTES_STORAGE_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  function getUserVote(reviewId) {
    const v = getStoredVotes()[reviewId];
    return v === 'up' || v === 'down' ? v : null;
  }

  function storeVote(reviewId, voteType) {
    const votes = getStoredVotes();
    votes[reviewId] = voteType;
    localStorage.setItem(VOTES_STORAGE_KEY, JSON.stringify(votes));
  }

  /* =========================================================
     SVG helpers
     ========================================================= */
  function starSVG(filled) {
    if (filled) {
      return '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" fill="#071835" stroke="#071835" stroke-width="1"/></svg>';
    }
    return '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" fill="none" stroke="#ccc" stroke-width="1.5"/></svg>';
  }

  function halfStarSVG() {
    return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <defs><clipPath id="half"><rect x="0" y="0" width="12" height="24"/></clipPath></defs>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" fill="none" stroke="#ccc" stroke-width="1.5"/>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" fill="#071835" stroke="#071835" stroke-width="1" clip-path="url(#half)"/>
    </svg>`;
  }

  function renderStars(rating, size) {
    const cls = size === 'large' ? 'star-icon star-icon--large' : 'star-icon';
    let html = '';
    const full = Math.floor(rating);
    const hasHalf = rating - full >= 0.25 && rating - full < 0.75;
    const roundUp = rating - full >= 0.75;
    for (let i = 1; i <= 5; i++) {
      if (i <= full || (roundUp && i <= full + 1)) {
        html += `<span class="${cls}">${starSVG(true)}</span>`;
      } else if (i === full + 1 && hasHalf) {
        html += `<span class="${cls}">${halfStarSVG()}</span>`;
      } else {
        html += `<span class="${cls}">${starSVG(false)}</span>`;
      }
    }
    return html;
  }

  function thumbUpSVG() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>';
  }

  function thumbDownSVG() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>';
  }

  /* =========================================================
     API calls
     ========================================================= */
  async function fetchReviews() {
    const params = new URLSearchParams({
      per_page: PER_PAGE,
      page: state.page,
      sort: state.sort,
      direction: state.direction,
    });
    if (state.starFilter) params.set('star', state.starFilter);

    const url = `${API_BASE}/${APP_KEY}/products/${PRODUCT_ID}/reviews.json?${params}`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      const resp = data.response;
      state.reviews = resp.reviews || [];
      state.totalReviews = resp.pagination?.total || 0;
      state.bottomline = resp.bottomline || null;
    } catch (e) {
      console.error('Failed to fetch reviews:', e);
      state.reviews = [];
      state.totalReviews = 0;
    }
  }

  async function fetchAllReviews() {
    if (state.allReviewsLoaded) return;
    const PAGE_SIZE = 150;
    const MAX_PAGES = 50;
    let page = 1;
    let all = [];
    let total = Infinity;
    let bottomline = null;
    while (all.length < total && page <= MAX_PAGES) {
      const params = new URLSearchParams({
        per_page: String(PAGE_SIZE),
        page: String(page),
      });
      const url = `${API_BASE}/${APP_KEY}/products/${PRODUCT_ID}/reviews.json?${params}`;
      try {
        const res = await fetch(url);
        const data = await res.json();
        const resp = data.response;
        const batch = resp.reviews || [];
        all = all.concat(batch);
        total = resp.pagination?.total ?? all.length;
        if (!bottomline) bottomline = resp.bottomline;
        if (batch.length < PAGE_SIZE) break;
        page++;
      } catch (e) {
        console.error('Failed to fetch all reviews:', e);
        break;
      }
    }
    state.allReviews = all;
    state.allReviewsLoaded = true;
    if (bottomline) state.bottomline = bottomline;
    state.totalReviews = total === Infinity ? all.length : total;
  }

  async function submitReview(formData) {
    const url = `${API_BASE}/reviews`;
    const body = {
      appkey: APP_KEY,
      domain: DOMAIN,
      sku: PRODUCT_ID,
      product_title: PRODUCT_TITLE,
      product_url: PRODUCT_URL,
      product_image_url: PRODUCT_IMAGE_URL,
      display_name: formData.name,
      email: formData.email,
      review_content: formData.body,
      review_title: formData.title,
      review_score: formData.rating,
    };
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      return { success: res.ok, data };
    } catch (e) {
      console.error('Failed to submit review:', e);
      return { success: false, error: e.message };
    }
  }

  // Uploads a single image to Cloudinary using an unsigned preset. Returns the
  // hosted `secure_url`. Yotpo's public API doesn't accept raw browser uploads;
  // it requires images to already be hosted at a public URL before they can be
  // attached to a review via /images/process.
  async function uploadImage(file) {
    if (!CLOUDINARY_CLOUD || !CLOUDINARY_PRESET) {
      throw new Error('Photo upload not configured');
    }
    const formData = new FormData();
    formData.append('upload_preset', CLOUDINARY_PRESET);
    formData.append('file', file);
    const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`;
    const res = await fetch(url, { method: 'POST', body: formData });
    if (!res.ok) throw new Error(`Cloudinary upload failed (${res.status})`);
    const data = await res.json().catch(() => ({}));
    const imageUrl = data.secure_url || data.url;
    if (!imageUrl) throw new Error('Cloudinary upload returned no URL');
    return imageUrl;
  }

  // Associates already-hosted image URLs with a freshly submitted Yotpo review
  // using the image_upload_token returned by POST /v1/widget/reviews.
  async function attachImagesToReview(token, imageUrls) {
    const res = await fetch('https://api.yotpo.com/images/process', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_upload_token: token, image_urls: imageUrls }),
    });
    if (!res.ok) throw new Error(`Image attach failed (${res.status})`);
  }

  async function voteReview(reviewId, voteType) {
    const url = `https://api.yotpo.com/reviews/${reviewId}/vote/${voteType}`;
    try {
      await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ appkey: APP_KEY }) });
    } catch (e) {
      console.error('Vote failed:', e);
    }
  }

  /* =========================================================
     Render
     ========================================================= */
  function render() {
    const bl = state.bottomline || {};
    const avg = bl.average_score || 0;
    const overallTotal = bl.total_review || state.totalReviews || 0;
    const dist = bl.star_distribution || {};

    // Recommend stats: prefer client-side aggregation from per-review sentiment
    // (works for imported reviews, which Yotpo excludes from total_organic_reviews).
    // Fall back to bottomline data when the full review list isn't loaded yet.
    let recommendCount = 0;
    let recommendTotal = 0;
    if (state.allReviewsLoaded && state.allReviews.length > 0) {
      recommendCount = state.allReviews.filter(r => r.sentiment > 0).length;
      recommendTotal = state.allReviews.length;
    } else if (bl.total_organic_reviews) {
      recommendCount = bl.organic_reviews_that_recommend || 0;
      recommendTotal = bl.total_organic_reviews;
    }
    const recommendPct = recommendTotal > 0
      ? Math.round((recommendCount / recommendTotal) * 100)
      : 0;

    let displayReviews;
    let filteredTotal;
    if (state.searchQuery) {
      const q = state.searchQuery.toLowerCase();
      let pool = state.allReviews.filter(r => {
        const content = (r.content || '').toLowerCase();
        const title = (r.title || '').toLowerCase();
        const name = (r.user?.display_name || '').toLowerCase();
        return content.includes(q) || title.includes(q) || name.includes(q);
      });
      if (state.starFilter != null) {
        pool = pool.filter(r => String(r.score) === String(state.starFilter));
      }
      pool = sortReviewsClient(pool);
      filteredTotal = pool.length;
      const start = (state.page - 1) * PER_PAGE;
      displayReviews = pool.slice(start, start + PER_PAGE);
    } else {
      filteredTotal = state.starFilter != null
        ? (Number(dist[state.starFilter]) || 0)
        : overallTotal;
      displayReviews = state.reviews;
    }

    const reviewCountLabel =
      overallTotal === 1 ? t('review_count_one', { count: overallTotal }) : t('review_count_other', { count: overallTotal });

    CONTAINER.innerHTML = `
      <h2 class="product-reviews__heading">${escapeHtml(t('heading'))}</h2>

      <!-- Summary Row -->
      <div class="product-reviews__summary">
        <!-- Rating Snapshot -->
        <div class="product-reviews__snapshot">
          <div class="product-reviews__snapshot-title">${escapeHtml(t('snapshot_title'))}</div>
          <div class="product-reviews__snapshot-subtitle">${escapeHtml(t('snapshot_subtitle'))}</div>
          ${[5, 4, 3, 2, 1].map(star => {
            const count = dist[star] || 0;
            const pct = overallTotal > 0 ? (count / overallTotal) * 100 : 0;
            const isActive = state.starFilter == star ? ' active' : '';
            const starLbl = escapeHtml(starLabel(star, 'snapshot_star_one', 'snapshot_star_other'));
            return `
              <div class="product-reviews__snapshot-row${isActive}" data-star="${star}">
                <span class="product-reviews__snapshot-label">${starLbl}</span>
                <div class="product-reviews__snapshot-bar">
                  <div class="product-reviews__snapshot-bar-fill" style="width:${pct}%"></div>
                </div>
                <span class="product-reviews__snapshot-count">${count}</span>
              </div>`;
          }).join('')}
        </div>

        <!-- Overall Rating -->
        <div class="product-reviews__overall">
          <div class="product-reviews__overall-title">${escapeHtml(t('overall_title'))}</div>
          <div class="product-reviews__overall-score-row">
            <span class="product-reviews__overall-number">${avg.toFixed(1)}</span>
            <div class="product-reviews__overall-stars-group">
              <div class="product-reviews__overall-stars">${renderStars(avg, 'large')}</div>
              <div class="product-reviews__overall-count">${escapeHtml(reviewCountLabel)}</div>
            </div>
          </div>
          ${recommendTotal > 0 ? `<div class="product-reviews__overall-recommend">${escapeHtml(t('recommend_summary', { count: recommendCount, total: recommendTotal, pct: recommendPct }))}</div>` : ''}
        </div>

        <!-- Write a Review -->
        <div class="product-reviews__write">
          <div class="product-reviews__write-title">${escapeHtml(t('write_title'))}</div>
          <div class="product-reviews__write-stars" id="review-write-stars">
            ${[1, 2, 3, 4, 5].map(i => {
              const aria = escapeHtml(starLabel(i, 'write_star_aria_one', 'write_star_aria_other'));
              return `
              <button class="product-reviews__write-star" data-rating="${i}" aria-label="${aria}">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              </button>`;
            }).join('')}
          </div>
          <div class="product-reviews__write-note">${escapeHtml(t('write_note_email'))}</div>
        </div>
      </div>

      <!-- Review Form Overlay -->
      <div class="product-reviews__form-overlay" id="review-form-overlay">
        <div class="product-reviews__form" id="review-form">
          <button class="product-reviews__form-close" id="review-form-close" aria-label="${escapeHtml(t('form_close_aria'))}">&times;</button>
          <h3>${escapeHtml(t('form_title'))}</h3>
          <div class="product-reviews__form-group">
            <label>${escapeHtml(t('your_rating'))} <span class="required">*</span></label>
            <div class="product-reviews__form-rating" id="form-rating-stars">
              ${[1, 2, 3, 4, 5].map(i => {
              const aria = escapeHtml(starLabel(i, 'form_rating_star_aria_one', 'form_rating_star_aria_other'));
              return `
                <button class="product-reviews__form-rating-star" data-rating="${i}" aria-label="${aria}">
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                </button>`;
            }).join('')}
            </div>
          </div>
          <div class="product-reviews__form-group">
            <label for="review-title">${escapeHtml(t('review_title_label'))} <span class="required">*</span></label>
            <input type="text" id="review-title" placeholder="${escapeHtml(t('review_title_placeholder'))}" required>
          </div>
          <div class="product-reviews__form-group">
            <label for="review-body">${escapeHtml(t('review_body_label'))} <span class="required">*</span></label>
            <textarea id="review-body" placeholder="${escapeHtml(t('review_body_placeholder'))}" required></textarea>
          </div>
          <div class="product-reviews__form-recommend">
            <input type="checkbox" id="review-recommend" checked>
            <label for="review-recommend">${escapeHtml(t('recommend_label'))}</label>
          </div>
          <div class="product-reviews__form-group">
            <label for="review-name">${escapeHtml(t('your_name'))} <span class="required">*</span></label>
            <input type="text" id="review-name" placeholder="${escapeHtml(t('name_placeholder'))}" required>
          </div>
          <div class="product-reviews__form-group">
            <label for="review-email">${escapeHtml(t('your_email'))} <span class="required">*</span></label>
            <input type="email" id="review-email" placeholder="${escapeHtml(t('email_placeholder'))}" required>
          </div>
          <div class="product-reviews__form-group">
            <label>Photos (optional)</label>
            <div class="product-reviews__photo-list" id="review-photo-list">
              ${state.selectedImages.map((img, idx) => `
                <div class="product-reviews__photo-thumb" data-idx="${idx}">
                  <img src="${img.url}" alt="">
                  <button type="button" class="product-reviews__photo-remove" aria-label="Remove photo">&times;</button>
                </div>`).join('')}
              ${state.selectedImages.length < MAX_IMAGES ? `
                <label class="product-reviews__photo-add" for="review-photo-input">
                  <span class="product-reviews__photo-add-icon">+</span>
                  <span>Add</span>
                </label>` : ''}
            </div>
            <input type="file" id="review-photo-input" accept="${ACCEPTED_IMAGE_TYPES.join(',')}" multiple hidden>
            <p class="product-reviews__photo-hint">Up to ${MAX_IMAGES} photos. JPG, PNG, or WebP. Max 5MB each.</p>
          </div>
          <div class="product-reviews__form-hp" aria-hidden="true">
            <label for="review-website">${escapeHtml(t('honeypot_website'))}</label>
            <input type="text" id="review-website" name="website" tabindex="-1" autocomplete="off">
          </div>
          <button class="product-reviews__form-submit" id="review-submit">${escapeHtml(t('submit_review'))}</button>
          <div class="product-reviews__form-message" id="review-form-message"></div>
        </div>
      </div>

      ${renderCustomerImages()}

      <!-- Filters -->
      <div class="product-reviews__filters">
        <div class="product-reviews__filters-title">${escapeHtml(t('filters_title'))}</div>
        <div class="product-reviews__filter-row">
          <div class="product-reviews__filter-select">
            <select id="filter-rating" aria-label="${escapeHtml(t('filter_rating_aria'))}">
              <option value="">${escapeHtml(t('rating_option'))}</option>
              ${[5, 4, 3, 2, 1]
                .map(
                  (n) =>
                    `<option value="${n}"${state.starFilter == n ? ' selected' : ''}>${escapeHtml(starLabel(n, 'rating_star', 'rating_stars'))}</option>`,
                )
                .join('')}
            </select>
          </div>
          <div class="product-reviews__filter-select">
            <select id="filter-employee" aria-label="${escapeHtml(t('filter_employee_aria'))}">
              <option value="">${escapeHtml(t('employee_review'))}</option>
              <option value="true">${escapeHtml(t('yes'))}</option>
              <option value="false">${escapeHtml(t('no'))}</option>
            </select>
          </div>
          <div class="product-reviews__search">
            <input type="text" id="review-search" placeholder="${escapeHtml(t('search_placeholder'))}" value="${escapeHtml(state.searchQuery)}">
            <span class="product-reviews__search-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="11" cy="11" r="8" stroke-width="2"/><line x1="21" y1="21" x2="16.65" y2="16.65" stroke-width="2"/></svg>
            </span>
          </div>
          <div class="product-reviews__sort">
            <div class="product-reviews__sort-wrapper">
              <select id="review-sort" aria-label="${escapeHtml(t('sort_aria'))}">
                <option value="date-desc"${state.sort === 'date' && state.direction === 'desc' ? ' selected' : ''}>${escapeHtml(t('sort_recent'))}</option>
                <option value="date-asc"${state.sort === 'date' && state.direction === 'asc' ? ' selected' : ''}>${escapeHtml(t('sort_oldest'))}</option>
                <option value="rating-desc"${state.sort === 'rating' && state.direction === 'desc' ? ' selected' : ''}>${escapeHtml(t('sort_rating_high'))}</option>
                <option value="rating-asc"${state.sort === 'rating' && state.direction === 'asc' ? ' selected' : ''}>${escapeHtml(t('sort_rating_low'))}</option>
                <option value="votes_up-desc"${state.sort === 'votes_up' && state.direction === 'desc' ? ' selected' : ''}>${escapeHtml(t('sort_helpful'))}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Reviews List -->
      ${state.loading
        ? `<div class="product-reviews__loading">${escapeHtml(t('loading'))}</div>`
        : displayReviews.length === 0
          ? `<div class="product-reviews__empty">${escapeHtml(t('empty'))}</div>`
          : `<div class="product-reviews__list">${displayReviews.map(renderReview).join('')}</div>`
      }

      <!-- Pagination -->
      ${renderPagination(filteredTotal)}
    `;

    bindEvents();
  }

  function renderReview(review) {
    const date = new Date(review.created_at);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    let dateStr;
    if (diffDays === 0) dateStr = t('date_today');
    else if (diffDays === 1) dateStr = t('date_yesterday');
    else if (diffDays < 30) dateStr = t('date_days_ago', { count: diffDays });
    else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      dateStr = months === 1 ? t('date_months_ago_one') : t('date_months_ago_other', { count: months });
    } else {
      dateStr = date.toLocaleDateString(LOCALE || 'en', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    const reviewer = review.user?.display_name || t('anonymous');
    const body = review.content || '';
    const title = review.title || '';
    const score = review.score || 0;
    const userVote = getUserVote(review.id);
    const votesUp = (review.votes_up || 0) + (userVote === 'up' ? 1 : 0);
    const votesDown = (review.votes_down || 0) + (userVote === 'down' ? 1 : 0);
    const sentiment = review.sentiment;
    const images = Array.isArray(review.images_data) ? review.images_data : [];

    return `
      <div class="product-reviews__review" data-review-id="${review.id}">
        <div class="product-reviews__review-stars">${renderStars(score)}</div>
        ${title ? `<div class="product-reviews__review-title">${escapeHtml(title)}</div>` : ''}
        <div class="product-reviews__review-meta">${escapeHtml(reviewer)}</div>
        <div class="product-reviews__review-date">${dateStr}</div>
        ${body ? `<div class="product-reviews__review-body">${escapeHtml(body)}</div>` : ''}
        ${images.length > 0 ? `
          <div class="product-reviews__review-images">
            ${images.map(img => {
              const full = img.original_url || img.image_url || img.url || '';
              const thumb = img.thumb_url || full;
              if (!full) return '';
              return `<a class="product-reviews__review-image" href="${escapeHtml(full)}" target="_blank" rel="noopener noreferrer"><img src="${escapeHtml(thumb)}" alt="" loading="lazy"></a>`;
            }).join('')}
          </div>` : ''}
        ${sentiment > 0 ? `
          <div class="product-reviews__review-recommend">
            <span class="product-reviews__review-recommend-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20 6L9 17l-5-5" stroke="#f9f6f2" stroke-width="3" fill="none"/></svg>
            </span>
            ${escapeHtml(t('review_recommend_badge'))}
          </div>` : ''}
        <div class="product-reviews__review-actions">
          <span>${escapeHtml(t('helpful_question'))}</span>
          <button class="product-reviews__review-action${userVote === 'up' ? ' voted' : ''}" data-vote="up" data-review-id="${review.id}"${userVote ? ' disabled' : ''} aria-pressed="${userVote === 'up'}">
            ${thumbUpSVG()} (${votesUp})
          </button>
          <button class="product-reviews__review-action${userVote === 'down' ? ' voted' : ''}" data-vote="down" data-review-id="${review.id}"${userVote ? ' disabled' : ''} aria-pressed="${userVote === 'down'}">
            ${thumbDownSVG()} (${votesDown})
          </button>
        </div>
      </div>`;
  }

  // Aggregates all photos from the loaded reviews into a horizontally
  // scrollable "Customer image" carousel using the theme's <slideshow-component>.
  // Renders nothing until state.allReviewsLoaded is true so we have the full set.
  function renderCustomerImages() {
    if (!state.allReviewsLoaded) return '';
    const images = [];
    for (const review of state.allReviews) {
      const arr = Array.isArray(review.images_data) ? review.images_data : [];
      for (const img of arr) {
        const full = img.original_url || img.image_url || img.url;
        if (!full) continue;
        images.push({ full, thumb: img.thumb_url || full });
      }
    }
    if (images.length === 0) return '';

    const timeline = images.map((_, i) => `--slide-${i}`).join(',');
    const slides = images.map((img, idx) => `
      <slideshow-slide ref="slides[]" aria-hidden="${idx === 0 ? 'false' : 'true'}" style="--slideshow-timeline: --slide-${idx};" class="resource-list__slide" slide-id="customer-img-${idx}">
        <a href="${escapeHtml(img.full)}" target="_blank" rel="noopener noreferrer" class="product-reviews__customer-image">
          <img src="${escapeHtml(img.thumb)}" alt="" loading="lazy">
        </a>
      </slideshow-slide>`).join('');

    return `
      <div class="product-reviews__customer-images">
        <h3 class="product-reviews__customer-images-title">Customer image</h3>
        <div class="resource-list__carousel" style="--slide-width-max: 200px; --resource-list-column-gap: 20px;">
          <slideshow-component ref="customerImagesCarousel" class="resource-list__carousel" style="--slideshow-timeline: ${timeline};" initial-slide="0">
            <slideshow-container ref="slideshowContainer">
              <slideshow-slides tabindex="-1" ref="scroller">${slides}</slideshow-slides>
            </slideshow-container>
          </slideshow-component>
        </div>
      </div>`;
  }

  function renderPagination(total) {
    const totalPages = Math.ceil(total / PER_PAGE);
    if (totalPages <= 1) return '';

    let html = '<div class="product-reviews__pagination">';
    html += `<button class="product-reviews__pagination-btn" data-page="${state.page - 1}" ${state.page <= 1 ? 'disabled' : ''}>&laquo;</button>`;

    const maxButtons = 5;
    let startPage = Math.max(1, state.page - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let p = startPage; p <= endPage; p++) {
      html += `<button class="product-reviews__pagination-btn${p === state.page ? ' active' : ''}" data-page="${p}">${p}</button>`;
    }

    html += `<button class="product-reviews__pagination-btn" data-page="${state.page + 1}" ${state.page >= totalPages ? 'disabled' : ''}>&raquo;</button>`;
    html += '</div>';
    return html;
  }

  /* =========================================================
     Client-side sort (used in search mode)
     ========================================================= */
  function sortReviewsClient(reviews) {
    const dir = state.direction === 'asc' ? 1 : -1;
    return [...reviews].sort((a, b) => {
      let va, vb;
      if (state.sort === 'rating') { va = a.score || 0; vb = b.score || 0; }
      else if (state.sort === 'votes_up') { va = a.votes_up || 0; vb = b.votes_up || 0; }
      else { va = new Date(a.created_at).getTime(); vb = new Date(b.created_at).getTime(); }
      return (va - vb) * dir;
    });
  }

  /* =========================================================
     Event binding
     ========================================================= */
  function bindEvents() {
    // Rating snapshot rows
    CONTAINER.querySelectorAll('.product-reviews__snapshot-row').forEach(row => {
      row.addEventListener('click', () => {
        const star = row.dataset.star;
        state.starFilter = state.starFilter == star ? null : star;
        state.page = 1;
        loadAndRender();
      });
    });

    // Rating filter dropdown
    const filterRating = document.getElementById('filter-rating');
    if (filterRating) {
      filterRating.addEventListener('change', () => {
        state.starFilter = filterRating.value || null;
        state.page = 1;
        loadAndRender();
      });
    }

    // Sort dropdown
    const sortSelect = document.getElementById('review-sort');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        const [sort, direction] = sortSelect.value.split('-');
        state.sort = sort;
        state.direction = direction;
        state.page = 1;
        loadAndRender();
      });
    }

    // Search
    const searchInput = document.getElementById('review-search');
    if (searchInput) {
      let searchTimeout;
      searchInput.addEventListener('input', () => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          const newQuery = searchInput.value.trim();
          if (newQuery === state.searchQuery) return;
          state.searchQuery = newQuery;
          state.page = 1;
          loadAndRender();
        }, 300);
      });
    }

    // Pagination
    CONTAINER.querySelectorAll('.product-reviews__pagination-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const page = parseInt(btn.dataset.page);
        if (page >= 1) {
          state.page = page;
          loadAndRender();
          CONTAINER.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    // Write review stars (open form)
    CONTAINER.querySelectorAll('#review-write-stars .product-reviews__write-star').forEach(star => {
      star.addEventListener('click', () => openReviewForm(parseInt(star.dataset.rating)));
      star.addEventListener('mouseenter', () => highlightWriteStars(parseInt(star.dataset.rating)));
    });
    const writeStarsContainer = document.getElementById('review-write-stars');
    if (writeStarsContainer) {
      writeStarsContainer.addEventListener('mouseleave', () => highlightWriteStars(0));
    }

    // Form overlay close
    const overlay = document.getElementById('review-form-overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeReviewForm();
      });
    }
    const closeBtn = document.getElementById('review-form-close');
    if (closeBtn) closeBtn.addEventListener('click', closeReviewForm);

    // Form rating stars
    CONTAINER.querySelectorAll('#form-rating-stars .product-reviews__form-rating-star').forEach(star => {
      star.addEventListener('click', () => {
        const rating = parseInt(star.dataset.rating);
        CONTAINER.querySelectorAll('#form-rating-stars .product-reviews__form-rating-star').forEach((s, idx) => {
          s.classList.toggle('active', idx < rating);
        });
        star.closest('.product-reviews__form-rating').dataset.selectedRating = rating;
      });
    });

    // Photo input + remove buttons (event-delegated since list re-renders)
    const photoInput = document.getElementById('review-photo-input');
    if (photoInput) {
      photoInput.addEventListener('change', () => {
        const files = Array.from(photoInput.files || []);
        const errors = addImageFiles(files);
        photoInput.value = ''; // allow re-selecting the same file
        renderPhotoList();
        const msgEl = document.getElementById('review-form-message');
        if (errors.length && msgEl) {
          msgEl.textContent = errors.join('; ');
          msgEl.className = 'product-reviews__form-message error';
        }
      });
    }
    const photoList = document.getElementById('review-photo-list');
    if (photoList) {
      photoList.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.product-reviews__photo-remove');
        if (!removeBtn) return;
        const thumb = removeBtn.closest('.product-reviews__photo-thumb');
        const idx = parseInt(thumb?.dataset.idx || '-1', 10);
        if (idx >= 0) {
          removeImageAt(idx);
          renderPhotoList();
        }
      });
    }

    // Form submit
    const submitBtn = document.getElementById('review-submit');
    if (submitBtn) submitBtn.addEventListener('click', handleSubmitReview);

    // Vote buttons
    CONTAINER.querySelectorAll('.product-reviews__review-action[data-vote]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const reviewId = btn.dataset.reviewId;
        if (getUserVote(reviewId)) return;
        const voteType = btn.dataset.vote;
        storeVote(reviewId, voteType);
        render();
        await voteReview(reviewId, voteType);
      });
    });
  }

  /* =========================================================
     Form helpers
     ========================================================= */
  let formInitialRating = 0;
  let formOpenedAt = 0;
  const MIN_SUBMIT_SECONDS = 3;

  function highlightWriteStars(rating) {
    CONTAINER.querySelectorAll('#review-write-stars .product-reviews__write-star').forEach((s, idx) => {
      s.classList.toggle('active', idx < rating);
    });
  }

  function openReviewForm(rating) {
    formInitialRating = rating;
    formOpenedAt = Date.now();
    const overlay = document.getElementById('review-form-overlay');
    if (overlay) overlay.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Set initial rating stars in form
    setTimeout(() => {
      CONTAINER.querySelectorAll('#form-rating-stars .product-reviews__form-rating-star').forEach((s, idx) => {
        s.classList.toggle('active', idx < rating);
      });
      const ratingContainer = document.querySelector('#form-rating-stars');
      if (ratingContainer) ratingContainer.dataset.selectedRating = rating;
    }, 0);
  }

  function closeReviewForm() {
    const overlay = document.getElementById('review-form-overlay');
    if (overlay) overlay.classList.remove('active');
    document.body.style.overflow = '';
    // Reset form
    const msg = document.getElementById('review-form-message');
    if (msg) { msg.textContent = ''; msg.className = 'product-reviews__form-message'; }
    clearSelectedImages();
    renderPhotoList();
  }

  async function handleSubmitReview() {
    const ratingContainer = document.querySelector('#form-rating-stars');
    const rating = parseInt(ratingContainer?.dataset.selectedRating || '0');
    const title = document.getElementById('review-title')?.value.trim();
    const body = document.getElementById('review-body')?.value.trim();
    const name = document.getElementById('review-name')?.value.trim();
    const email = document.getElementById('review-email')?.value.trim();
    const msgEl = document.getElementById('review-form-message');
    const submitBtn = document.getElementById('review-submit');

    if (!rating || !title || !body || !name || !email) {
      if (msgEl) {
        msgEl.textContent = t('required_fields');
        msgEl.className = 'product-reviews__form-message error';
      }
      return;
    }

    // Anti-spam guards: honeypot field + minimum time-to-submit.
    // Bots typically autofill hidden inputs and submit near-instantly.
    // On trigger, we fake a success response and silently skip the API call,
    // so adaptive bots don't learn from the failure.
    const honeypot = document.getElementById('review-website')?.value || '';
    const secondsOpen = (Date.now() - formOpenedAt) / 1000;
    if (honeypot || secondsOpen < MIN_SUBMIT_SECONDS) {
      if (msgEl) {
        msgEl.textContent = t('thank_you_pending');
        msgEl.className = 'product-reviews__form-message success';
      }
      setTimeout(closeReviewForm, 2000);
      return;
    }

    if (submitBtn) submitBtn.disabled = true;

    // Step 1: upload photos to Cloudinary so we have public URLs to hand to
    // Yotpo. We do this BEFORE submitting the review so a Cloudinary failure
    // doesn't leave an orphan photo-less review in the moderation queue.
    let imageUrls = [];
    if (state.selectedImages.length > 0) {
      if (msgEl) { msgEl.textContent = 'Uploading photos...'; msgEl.className = 'product-reviews__form-message'; }
      try {
        imageUrls = await Promise.all(state.selectedImages.map(img => uploadImage(img.file)));
      } catch (e) {
        if (msgEl) {
          msgEl.textContent = `Photo upload failed: ${e.message}`;
          msgEl.className = 'product-reviews__form-message error';
        }
        if (submitBtn) submitBtn.disabled = false;
        return;
      }
    }

    if (msgEl) { msgEl.textContent = t('submitting'); msgEl.className = 'product-reviews__form-message'; }

    // Step 2: submit the review (no images in body — Yotpo's two-step flow).
    const result = await submitReview({ rating, title, body, name, email });

    // Step 3: if there are photos and the review POST returned an
    // image_upload_token, associate the Cloudinary URLs with the review.
    let imageAttachFailed = false;
    if (result.success && imageUrls.length > 0) {
      const token = result.data?.response?.image_upload_token
        || result.data?.image_upload_token;
      if (token) {
        try {
          await attachImagesToReview(token, imageUrls);
        } catch (e) {
          console.error('Image attach failed:', e);
          imageAttachFailed = true;
        }
      } else {
        console.error('No image_upload_token in review response:', result.data);
        imageAttachFailed = true;
      }
    }

    if (result.success) {
      if (msgEl) {
        msgEl.textContent = imageAttachFailed
          ? 'Review submitted. Photos are still processing — they may take a few minutes to appear.'
          : t('thank_you_pending');
        msgEl.className = 'product-reviews__form-message success';
      }
      // Clear form fields
      document.getElementById('review-title').value = '';
      document.getElementById('review-body').value = '';
      document.getElementById('review-name').value = '';
      document.getElementById('review-email').value = '';
      setTimeout(() => {
        closeReviewForm();
        loadAndRender();
      }, 2000);
    } else {
      if (msgEl) {
        msgEl.textContent = t('error_generic');
        msgEl.className = 'product-reviews__form-message error';
      }
    }
    if (submitBtn) submitBtn.disabled = false;
  }

  /* =========================================================
     Utilities
     ========================================================= */
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* =========================================================
     Load and render
     ========================================================= */
  async function loadAndRender() {
    state.loading = true;
    render();
    if (state.searchQuery) {
      await fetchAllReviews();
    } else {
      await fetchReviews();
    }
    state.loading = false;
    render();

    // Ensure we have the full review list so render() can compute aggregate
    // stats client-side (e.g. recommend count from per-review sentiment, which
    // Yotpo's bottomline omits when total_organic_reviews is 0).
    if (!state.allReviewsLoaded && !state.searchQuery) {
      if (state.totalReviews > 0 && state.reviews.length >= state.totalReviews) {
        state.allReviews = [...state.reviews];
        state.allReviewsLoaded = true;
        render();
      } else {
        fetchAllReviews().then(() => render());
      }
    }
  }

  // Initial load
  loadAndRender();
})();
